<script setup lang="ts">
/**
 * BoutiqueCartDrawer — panneau latéral « panier », le cœur du parcours d'achat.
 *
 * ⚠️ POURQUOI CE COMPOSANT EXISTE.
 * Avant lui, ajouter un produit depuis sa fiche ne donnait AUCUN retour
 * exploitable : il fallait revenir sur la vitrine, puis faire défiler jusqu'au
 * bloc panier situé tout en bas de l'étape 1. Aucune page ne montrait le panier
 * en permanence. Le panneau s'ouvre donc AUTOMATIQUEMENT à chaque ajout — la
 * convention e-commerce (Shopify, Sézane, Decathlon) : l'acheteur voit ce qu'il
 * vient de mettre, son total, et décide sur-le-champ de continuer ou de
 * commander.
 *
 * Il est monté par la VITRINE et par la FICHE PRODUIT, avec le même panier
 * (`useShopCart`, sessionStorage par boutique) et le même état d'ouverture
 * (`useShopCartDrawer`) : un seul composant, deux points de montage, zéro
 * duplication.
 *
 * Accessibilité (le panneau est une vraie boîte de dialogue) :
 *  - `role="dialog"` + `aria-modal` + `aria-label` ;
 *  - focus déplacé DANS le panneau à l'ouverture, PIÉGÉ tant qu'il est ouvert
 *    (Tab / Shift+Tab bouclent), et RENDU au déclencheur à la fermeture ;
 *  - fermeture par Échap, par la croix, par clic sur le voile ;
 *  - scroll de la page verrouillé via le verrou PARTAGÉ à compteur.
 *
 * Couleurs : aucune couleur semi-transparente (règle `ui_no_bg_transparency`),
 * à la seule exception du VOILE de fond, qui est par nature un backdrop — même
 * traitement que `ImageLightbox` (`bg-black/70 backdrop-blur`).
 */
import type {
  ShopCartLineView,
  ShopProduct,
  ShopProductVariant,
} from "~/types/shop";

// Montants et dates dans la langue de la page.
const { formatCurrency } = useFormatLangue();

const props = defineProps<{
  cartLines: ShopCartLineView[];
  totalItems: number;
  subtotal: number;
  /** Destination du bouton « Commander » (page panier + query d'habillage). */
  cartPageLink: { path: string; query: Record<string, string> };
  /** Achat autorisé (fenêtre de vente ouverte) — sinon pas de « Commander ». */
  canBuy: boolean;
  /** Message de clôture / d'ouverture différée, affiché tel quel. */
  saleClosedMessage: string | null;
  /** Plafond courant d'une ligne (stock + max_per_order), hors la ligne. */
  getMaxForLine: (
    product: ShopProduct,
    variant: ShopProductVariant | null,
    excludeUid?: string,
  ) => number;
}>();

const emit = defineEmits<{
  (e: "update-quantity", uid: string, quantity: number): void;
  (e: "remove-line", uid: string): void;
}>();

const { isOpen, close } = useShopCartDrawer();

const panelRef = ref<HTMLElement | null>(null);
const closeButtonRef = ref<HTMLElement | null>(null);

const isEmpty = computed(() => props.cartLines.length === 0);

/** Verrou du scroll de la page — verrou partagé à compteur (empilement OK). */
useBodyScrollLock().bindToRef(() => isOpen.value);

/** Éléments focusables actuellement présents DANS le panneau. */
function focusables(): HTMLElement[] {
  const root = panelRef.value;
  if (!root) return [];
  return Array.from(
    root.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((el) => el.offsetParent !== null);
}

/**
 * Piège à focus : Tab sur le dernier élément revient au premier, Shift+Tab sur
 * le premier va au dernier. Sans ça, la tabulation sort du panneau et parcourt
 * la page masquée derrière le voile.
 */
function onKeydown(event: KeyboardEvent) {
  if (!isOpen.value) return;
  if (event.key === "Escape") {
    event.preventDefault();
    close();
    return;
  }
  if (event.key !== "Tab") return;
  const items = focusables();
  if (items.length === 0) return;
  const first = items[0]!;
  const last = items[items.length - 1]!;
  const active = document.activeElement as HTMLElement | null;
  if (event.shiftKey && (active === first || !panelRef.value?.contains(active))) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}

// Focus initial : la croix de fermeture. C'est l'action la moins destructrice
// (l'acheteur qui tabule juste après tombe sur « Commander »).
watch(isOpen, async (open) => {
  if (!open) return;
  await nextTick();
  closeButtonRef.value?.focus({ preventScroll: true });
});

onMounted(() => window.addEventListener("keydown", onKeydown));
onBeforeUnmount(() => window.removeEventListener("keydown", onKeydown));

/** « Commander » quitte le panneau : il ne doit pas rester ouvert par-dessus. */
function onCheckout() {
  close();
}
</script>

<template>
  <Teleport to="body">
    <Transition name="cart-drawer">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-[90] flex justify-end bg-black/60 backdrop-blur-sm"
        @click.self="close"
      >
        <aside
          ref="panelRef"
          role="dialog"
          aria-modal="true"
          :aria-label="$t('boutique.cartDrawer.titre')"
          class="cart-drawer-panel flex h-full w-full max-w-md flex-col bg-white dark:bg-[#141414] shadow-[0_0_40px_rgba(45,46,92,0.25)] sm:rounded-l-3xl"
        >
          <!-- En-tête : titre + compteur + fermeture -->
          <header
            class="flex items-center justify-between gap-3 border-b border-gray-100 dark:border-[#262626] px-5 py-4"
          >
            <div class="min-w-0">
              <h2
                class="flex items-center gap-2 text-base font-bold text-[#2d2e5c] dark:text-white"
              >
                <UIcon name="i-lucide-shopping-cart" class="size-4.5 shrink-0" />
                {{ $t("boutique.cartDrawer.titre") }}
              </h2>
              <p class="mt-0.5 text-xs text-[#717198] dark:text-gray-400">
                {{ totalItems }} article{{ totalItems > 1 ? "s" : "" }}
              </p>
            </div>
            <button
              ref="closeButtonRef"
              type="button"
              :aria-label="$t('boutique.cartDrawer.ariaFermer')"
              class="flex size-11 shrink-0 items-center justify-center rounded-xl text-[#505870] dark:text-gray-300 transition-colors hover:bg-gray-100 dark:hover:bg-[#1e1e1e]"
              @click="close"
            >
              <UIcon name="i-lucide-x" class="size-5" />
            </button>
          </header>

          <!-- Corps : lignes modifiables, ou état vide -->
          <div class="flex-1 overflow-y-auto overscroll-contain px-5 py-4">
            <BslugStatusNotice
              v-if="saleClosedMessage"
              class="mb-4"
              variant="inline"
              tone="red"
              icon="i-lucide-clock"
              :message="saleClosedMessage"
            />

            <!-- Panier vide : jamais un panneau muet, toujours une issue. -->
            <div
              v-if="isEmpty"
              class="flex h-full flex-col items-center justify-center px-2 text-center"
            >
              <UIcon
                name="i-lucide-shopping-cart"
                class="mb-3 size-10 text-gray-300 dark:text-gray-600"
              />
              <p class="text-sm font-semibold text-[#2d2e5c] dark:text-white">
                {{ $t("boutique.cartDrawer.panierVide") }}
              </p>
              <p class="mt-1 max-w-xs text-sm text-[#717198] dark:text-gray-400">
                {{ $t("boutique.cartDrawer.panierVideDescription") }}
              </p>
              <button
                type="button"
                class="mt-5 inline-flex min-h-11 items-center gap-2 rounded-xl px-5 text-sm font-semibold accent-btn"
                @click="close"
              >
                <UIcon name="i-lucide-store" class="size-4" />
                {{ $t("boutique.cartDrawer.voirProduits") }}
              </button>
            </div>

            <BoutiqueCartLines
              v-else
              :cart-lines="cartLines"
              :editable="true"
              :get-max-for-line="getMaxForLine"
              @update-quantity="(uid, q) => emit('update-quantity', uid, q)"
              @remove-line="(uid) => emit('remove-line', uid)"
            />
          </div>

          <!-- Pied : sous-total + commander + continuer mes achats -->
          <footer
            v-if="!isEmpty"
            class="border-t border-gray-100 dark:border-[#262626] bg-gray-50 dark:bg-[#0f0f0f] px-5 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))]"
          >
            <div class="flex items-center justify-between">
              <span class="text-sm text-[#505870] dark:text-gray-400">
                {{ $t("boutique.cartDrawer.sousTotal") }}
              </span>
              <span
                class="text-lg font-bold tabular-nums text-[#2d2e5c] dark:text-white"
              >
                {{ subtotal > 0 ? formatCurrency(subtotal) : $t("boutique.commun.gratuit") }}
              </span>
            </div>
            <!-- Le port et le don se choisissent à la commande : le dire ici
                 évite la « mauvaise surprise » (Baymard : frais découverts
                 tard = cause n°1 d'abandon). -->
            <p class="mt-1 text-xs text-[#717198] dark:text-gray-500">
              {{ $t("boutique.cartDrawer.livraisonEtDonInfo") }}
            </p>

            <NuxtLink
              v-if="canBuy"
              :to="cartPageLink"
              data-testid="drawer-checkout"
              class="mt-4 flex min-h-12 w-full items-center justify-center gap-2 rounded-xl text-sm font-semibold accent-btn"
              @click="onCheckout"
            >
              {{ $t("boutique.commun.commander") }}
              <UIcon name="i-lucide-arrow-right" class="size-4" />
            </NuxtLink>

            <button
              type="button"
              class="mt-2 flex min-h-11 w-full items-center justify-center rounded-xl bg-main text-sm font-medium text-white transition-colors hover:bg-main-deep"
              @click="close"
            >
              {{ $t("boutique.cartDrawer.continuerAchats") }}
            </button>
          </footer>
        </aside>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.accent-btn {
  background: var(--accent, #212121);
  color: var(--accent-text, #fff);
}
/* Survol en COULEUR PLEINE (règle `ui_no_bg_transparency`) : on assombrit
   l'accent de 12 % au lieu de le rendre translucide. */
.accent-btn:hover {
  background: color-mix(in srgb, var(--accent, #212121) 88%, #000);
}

/* Voile : fondu. Panneau : glissement depuis la droite.
   Le panneau occupe toute la largeur sous `sm` (bottom-sheet plein écran, où
   un demi-écran serait illisible) et une colonne de 28rem au-delà. */
.cart-drawer-enter-active {
  transition: opacity 0.25s ease-out;
}
.cart-drawer-leave-active {
  transition: opacity 0.2s ease-in;
}
.cart-drawer-enter-from,
.cart-drawer-leave-to {
  opacity: 0;
}

.cart-drawer-enter-active .cart-drawer-panel {
  transition: transform 0.3s cubic-bezier(0.32, 0.72, 0, 1);
}
.cart-drawer-leave-active .cart-drawer-panel {
  transition: transform 0.2s ease-in;
}
.cart-drawer-enter-from .cart-drawer-panel,
.cart-drawer-leave-to .cart-drawer-panel {
  transform: translateX(100%);
}

@media (prefers-reduced-motion: reduce) {
  .cart-drawer-enter-active .cart-drawer-panel,
  .cart-drawer-leave-active .cart-drawer-panel {
    transition: none;
  }
}
</style>
